/**
 * Пиксельный буфер: рисуем формами (прямоугольник, эллипс, линия), потом обводим силуэт — так из кода получается
 * пиксель-арт без файлов картинок (решение владельца 2026-10-22: «рисуем сами»). Готовый буфер — offscreen canvas.
 */
export class Px {
  readonly w: number;
  readonly h: number;
  private readonly data: (string | null)[];
  private cached: HTMLCanvasElement | null = null;

  constructor(w: number, h: number) {
    this.w = w;
    this.h = h;
    this.data = new Array<string | null>(w * h).fill(null);
  }

  set(x: number, y: number, c: string | null): this {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    if (xi < 0 || yi < 0 || xi >= this.w || yi >= this.h) return this;
    this.data[yi * this.w + xi] = c;
    this.cached = null;
    return this;
  }

  get(x: number, y: number): string | null {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return null;
    return this.data[y * this.w + x];
  }

  rect(x: number, y: number, w: number, h: number, c: string | null): this {
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) this.set(x + i, y + j, c);
    return this;
  }

  /** Эллипс по центрам пикселей: (cx, cy) — в пиксельных координатах, можно с половинками. */
  ellipse(cx: number, cy: number, rx: number, ry: number, c: string | null, only?: (x: number, y: number) => boolean): this {
    const x0 = Math.floor(cx - rx - 1);
    const x1 = Math.ceil(cx + rx + 1);
    const y0 = Math.floor(cy - ry - 1);
    const y1 = Math.ceil(cy + ry + 1);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const dx = (x + 0.5 - cx) / rx;
        const dy = (y + 0.5 - cy) / ry;
        if (dx * dx + dy * dy <= 1 && (!only || only(x, y))) this.set(x, y, c);
      }
    }
    return this;
  }

  line(x0: number, y0: number, x1: number, y1: number, c: string, thick = 1): this {
    const steps = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let i = 0; i <= steps; i++) {
      const x = Math.round(x0 + ((x1 - x0) * i) / steps);
      const y = Math.round(y0 + ((y1 - y0) * i) / steps);
      this.rect(x, y, thick, thick, c);
    }
    return this;
  }

  /** Обводка снаружи силуэта: пустой пиксель рядом с закрашенным становится цветом обводки. */
  outline(c: string, diagonal = false): this {
    const add: number[] = [];
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        if (this.data[y * this.w + x] !== null) continue;
        const near =
          this.get(x - 1, y) !== null ||
          this.get(x + 1, y) !== null ||
          this.get(x, y - 1) !== null ||
          this.get(x, y + 1) !== null ||
          (diagonal &&
            (this.get(x - 1, y - 1) !== null ||
              this.get(x + 1, y - 1) !== null ||
              this.get(x - 1, y + 1) !== null ||
              this.get(x + 1, y + 1) !== null));
        if (near) add.push(y * this.w + x);
      }
    }
    for (const i of add) this.data[i] = c;
    this.cached = null;
    return this;
  }

  /** Перекрасить все пиксели (для вспышки удара — белый силуэт). */
  tint(c: string): Px {
    const copy = new Px(this.w, this.h);
    for (let i = 0; i < this.data.length; i++) if (this.data[i] !== null) copy.data[i] = c;
    return copy;
  }

  canvas(): HTMLCanvasElement {
    if (this.cached) return this.cached;
    const cv = document.createElement('canvas');
    cv.width = this.w;
    cv.height = this.h;
    const ctx = cv.getContext('2d');
    if (ctx) {
      for (let y = 0; y < this.h; y++) {
        for (let x = 0; x < this.w; x++) {
          const c = this.data[y * this.w + x];
          if (c === null) continue;
          ctx.fillStyle = c;
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
    this.cached = cv;
    return cv;
  }
}

/** Детерминированный «шум» по координатам: текстура травы и мостовой одинакова при каждой отрисовке. */
export function hash2(x: number, y: number, seed = 0): number {
  let h = (x * 374761393 + y * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

/** Зеркальная копия canvas по горизонтали (персонаж, идущий вправо). */
export function flipped(src: HTMLCanvasElement): HTMLCanvasElement {
  const cv = document.createElement('canvas');
  cv.width = src.width;
  cv.height = src.height;
  const ctx = cv.getContext('2d');
  if (ctx) {
    ctx.translate(src.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(src, 0, 0);
  }
  return cv;
}
